import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AIClipTaskSummary } from '../../../api/aiClip';
import HoverHint from '../../../components/HoverHint';

interface AIClipTaskActionCardProps {
  activeTask: AIClipTaskSummary | null;
  gateReady: boolean;
  isTriggering: boolean;
  isCancelling: boolean;
  onTrigger: () => Promise<void>;
  onCancel: (taskId: string) => Promise<void>;
}

type Confirmation = 'trigger' | 'cancel' | null;

const AIClipTaskActionCard = ({
  activeTask,
  gateReady,
  isTriggering,
  isCancelling,
  onTrigger,
  onCancel,
}: AIClipTaskActionCardProps) => {
  const { t, i18n } = useTranslation('episodeDetail');
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (confirmation) confirmRef.current?.focus();
  }, [confirmation]);

  const closeConfirmation = (kind: Exclude<Confirmation, null>) => {
    setConfirmation(null);
    (kind === 'trigger' ? triggerRef : cancelRef).current?.focus();
  };

  const confirmTrigger = async () => {
    await onTrigger();
    setConfirmation(null);
    sectionRef.current?.focus();
  };

  const confirmCancel = async () => {
    if (!activeTask) return;
    await onCancel(activeTask.id);
    setConfirmation(null);
    sectionRef.current?.focus();
  };

  let content;
  if (!activeTask) {
    content = (
      <>
        <div className="ai-clip-action-card__row">
          <div className="ai-clip-action-card__summary">
            <span className="ai-clip-action-card__dot" aria-hidden="true" />
            <strong>{t('aiClip.workspace.ready')}</strong>
          </div>
          <HoverHint hint={!gateReady ? t('aiClip.gateNotMet') : undefined} cursor={!gateReady ? 'not-allowed' : undefined}>
            <button
              ref={triggerRef}
              type="button"
              className="btn btn-primary ai-clip-action-card__button"
              disabled={!gateReady || isTriggering}
              style={!gateReady ? { pointerEvents: 'none' } : undefined}
              aria-expanded={confirmation === 'trigger'}
              onClick={() => setConfirmation('trigger')}
            >
              {t('aiClip.workspace.trigger')}
            </button>
          </HoverHint>
        </div>
        {confirmation === 'trigger' && (
          <InlineConfirmation
            message={t('aiClip.betaWarning')}
            confirmLabel={t('aiClip.workspace.confirmTrigger')}
            busy={isTriggering}
            confirmRef={confirmRef}
            onConfirm={confirmTrigger}
            onBack={() => closeConfirmation('trigger')}
          />
        )}
      </>
    );
  } else if (activeTask.cancelRequestedAt) {
    content = <ProgressLabel busy>{t('aiClip.workspace.cancelling')}</ProgressLabel>;
  } else if (activeTask.status === 'submitting') {
    content = <ProgressLabel busy>{t('aiClip.workspace.submitting')}</ProgressLabel>;
  } else if (activeTask.status === 'transferring') {
    content = <ProgressLabel busy>{t('aiClip.workspace.transferring')}</ProgressLabel>;
  } else if (activeTask.status === 'queued') {
    content = (
      <>
        <div className="ai-clip-action-card__row">
          <div className="ai-clip-action-card__active">
            <ProgressLabel busy>{activeTask.stage || t(`aiClip.workspace.status.${activeTask.status}`)}</ProgressLabel>
            <span className="ai-clip-action-card__time">
              {t('aiClip.workspace.submittedAt')}:{' '}
              <time dateTime={activeTask.createdAt}>
                {new Date(activeTask.createdAt).toLocaleString(i18n.language)}
              </time>
            </span>
          </div>
          <button
            ref={cancelRef}
            type="button"
            className="btn btn-secondary ai-clip-action-card__button"
            disabled={isCancelling}
            aria-expanded={confirmation === 'cancel'}
            onClick={() => setConfirmation('cancel')}
          >
            {t('aiClip.workspace.cancel')}
          </button>
        </div>
        {confirmation === 'cancel' && (
          <InlineConfirmation
            message={t('aiClip.workspace.cancelWarning')}
            confirmLabel={t('aiClip.workspace.confirmCancel')}
            busy={isCancelling}
            confirmRef={confirmRef}
            onConfirm={confirmCancel}
            onBack={() => closeConfirmation('cancel')}
          />
        )}
      </>
    );
  } else if (activeTask.status === 'running') {
    content = (
      <div className="ai-clip-action-card__active">
        <ProgressLabel busy>{activeTask.stage || t('aiClip.workspace.status.running')}</ProgressLabel>
        <span className="ai-clip-action-card__time">
          {t('aiClip.workspace.submittedAt')}:{' '}
          <time dateTime={activeTask.createdAt}>
            {new Date(activeTask.createdAt).toLocaleString(i18n.language)}
          </time>
        </span>
      </div>
    );
  } else {
    content = null;
  }

  return (
    <section
      ref={sectionRef}
      tabIndex={-1}
      aria-label={t('aiClip.workspace.title')}
      className="ai-clip-action-card"
    >
      {content}
    </section>
  );
};

function ProgressLabel({ children, busy }: { children: React.ReactNode; busy?: boolean }) {
  return (
    <span aria-busy={busy} className="ai-clip-progress-label">
      <span className="ai-clip-progress-label__pulse" aria-hidden="true" />
      <strong>{children}</strong>
    </span>
  );
}

interface InlineConfirmationProps {
  message: string;
  confirmLabel: string;
  busy: boolean;
  confirmRef: React.RefObject<HTMLButtonElement>;
  onConfirm: () => Promise<void>;
  onBack: () => void;
}

function InlineConfirmation({
  message, confirmLabel, busy, confirmRef, onConfirm, onBack,
}: InlineConfirmationProps) {
  const { t } = useTranslation('episodeDetail');
  return (
    <div className="ai-clip-inline-confirmation">
      <p>{message}</p>
      <div className="ai-clip-inline-confirmation__actions">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onBack}>
          {t('aiClip.cancel')}
        </button>
        <button
          ref={confirmRef}
          type="button"
          className="btn btn-primary"
          disabled={busy}
          aria-busy={busy}
          onClick={() => { void onConfirm(); }}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}

export default AIClipTaskActionCard;
